import moment from "moment-timezone";
import { LoadingSpinner } from "pepsico-ds";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";
import { GlobalConfigContext } from "../../context/GlobalConfigContext";
import { capitalizeFirstLetter, convertToTitleCase } from "../../utils/commons";
import ButtonGroupDynamic from "../common/ButtonGroupDynamic";
import "./performanceDetails.scss";
import PerformanceDetailsSection from "./PerformanceDetailsSection";

const sectionTitle = (engagementType) => {
  const type = engagementType?.toLowerCase() || "";
  const individualWords = type.split("-");
  const capitalizedWords = individualWords
    .map((word) => capitalizeFirstLetter(word))
    .join(" ");
  return `${capitalizedWords} details`;
};

const formatAdditionalInfo = (additionalInfo) => {
  if (!additionalInfo || !Array.isArray(additionalInfo)) return [];

  const formattedItems = [];

  additionalInfo.forEach((item) => {
    const { key, label, value } = item;

    if (key === "survey" && Array.isArray(value)) {
      formattedItems.push({
        key: `survey-${label}`,
        label: label || "Survey",
        value: value.map((v) => ({ key: v.question, value: v.answer })),
        type: "survey",
      });
    } else if (key === "image" && Array.isArray(value)) {
      value.forEach((v, index) => {
        formattedItems.push({
          key: `image-${label}-${index}`,
          label: label || "Image",
          value: v.url || "-",
          type: "image",
        });
      });
    } else if (key === "video" && Array.isArray(value)) {
      value.forEach((v, index) => {
        formattedItems.push({
          key: `video-${label}-${index}`,
          label: label || "Video",
          value: v.url || "-",
          type: "video",
        });
      });
    } else if (key === "audio" && Array.isArray(value)) {
      value.forEach((v, index) => {
        formattedItems.push({
          key: `audio-${label}-${index}`,
          label: label || "Audio",
          value: v.url || "-",
          type: "audio",
        });
      });
    }
  });

  return formattedItems;
};
const formatEngagmentTypeDetails = (data, programTimezone) => {
  const filterValidEntries = (entries) =>
    entries.filter((entry) => entry.value !== null && entry.value !== undefined);
  const engagementType = data?.engagementType || "";

  if (engagementType === "receipt-scan") {
    return {
      title: sectionTitle(data.engagementType),
      details: filterValidEntries([
        {
          key: "createdAt",
          label: "Scan Date (UTC-6)",
          value:
            data.metadata?.receiptData?.updated_at && programTimezone
              ? moment
                  .tz(data.metadata?.receiptData?.updated_at, programTimezone)
                  .format("Do MMMM YYYY hh:mm A")
              : "-",
        },
        {
          key: "billDate",
          label: "Receipt Date (UTC-6)",
          value:
            data.metadata?.receiptData?.receipt_data?.bill_date && programTimezone
              ? moment
                  .tz(
                    data.metadata?.receiptData?.receipt_data?.bill_date,
                    programTimezone
                  )
                  .format("Do MMMM YYYY hh:mm A")
              : "-",
        },
        {
          key: "retailer",
          label: "Retailer",
          value: data.metadata?.receiptData?.receipt_data?.vendor?.name || "-",
        },
        {
          key: "receiptTotal",
          label: "Receipt Total",
          value: data.metadata?.receiptData?.receipt_data?.total || "-",
        },
        {
          key: "fraudScore",
          label: "Fraud Score",
          value: data.metadata?.receiptData?.fraud?.score ?? "-",
        },
        {
          key: "tags",
          label: "Tags",
          value:
            data.metadata?.receiptData?.receipt_data?.tags?.map((tag) => tag.name) ||
            "-",
        },
        { key: "image", label: "Image", value: data.image },
        {
          key: "json",
          label: "JSON",
          value: data.metadata?.receiptData?.receipt_source?.source_metadata || "-",
        },
      ]),
    };
  } else if (engagementType === "package-scan") {
    return {
      title: sectionTitle(data.engagementType),
      details: filterValidEntries([
        {
          key: "scanDate",
          label: "Scan Date (UTC-6)",
          value:
            data.metadata?.packageData?.scan_date && programTimezone
              ? moment
                  .tz(data.metadata?.packageData?.scan_date, programTimezone)
                  .format("Do MMMM YYYY hh:mm A")
              : "-",
        },
        {
          key: "packageCode",
          label: "Code",
          value: data.metadata?.packageData?.package_code || "-",
        },
        { key: "sku", label: "SKU", value: data.metadata?.packageData?.sku || "-" },
        {
          key: "freshnessDate",
          label: "Fresh-By Date",
          value:
            data.metadata?.packageData?.freshness_date && programTimezone
              ? moment
                  .tz(data.metadata?.packageData?.freshness_date, programTimezone)
                  .format("Do MMMM YYYY hh:mm A")
              : "-",
        },
        { key: "image", label: "Image", value: data.image || "-" },
        {
          key: "sourceMetadata",
          label: "JSON",
          value: data.metadata?.packageData?.source?.source_metadata || "-",
        },
      ]),
    };
  } else if (engagementType === "image-submission") {
    const chipsBrand =
      data.metadata?.packageData?.source?.source_metadata?.extraction?.cpg_fields
        ?.chips_brand;
    const beverageBrand =
      data.metadata?.packageData?.source?.source_metadata?.extraction?.cpg_fields
        ?.beverage_brand;
    const detectedBrand = chipsBrand || beverageBrand || "-";

    return {
      title: sectionTitle(data.engagementType),
      details: filterValidEntries([
        {
          key: "submissionDate",
          label: "Submission Date (UTC-6)",
          value:
            data.metadata?.packageData?.created_at && programTimezone
              ? moment
                  .tz(data.metadata?.packageData?.created_at, programTimezone)
                  .format("Do MMMM YYYY hh:mm A")
              : "-",
        },
        {
          key: "detectedBrand",
          label: "Detected Brand",
          value: detectedBrand,
        },
        {
          key: "detectedLogos",
          label: "Detected Logos",
          value: data.metadata?.packageData?.detected_logos?.length
            ? data.metadata.packageData.detected_logos.join(", ")
            : "-",
        },
        {
          key: "extractedText",
          label: "Extracted Text",
          value: data.metadata?.packageData?.extracted_text?.length
            ? data.metadata.packageData.extracted_text.join(", ")
            : "-",
        },
        {
          key: "imageUrl",
          label: "Image",
          value: data.metadata?.packageData?.url || data.image || "-",
        },
        {
          key: "sourceMetadata",
          label: "JSON",
          value: data.metadata?.packageData?.source?.source_metadata || "-",
        },
      ]),
    };
  } else if (engagementType === "check-in") {
    return {
      title: sectionTitle(data.engagementType),
      details: filterValidEntries([
        {
          key: "identifier",
          label: "Referral Identifier",
          value: data.identifier || "-",
        },
        {
          key: "geolocation",
          label: "Geolocation",
          value:
            data.metadata?.latitude && data.metadata?.longitude
              ? `${data.metadata?.latitude}, ${data.metadata?.longitude}`
              : "-",
        },
      ]),
    };
  }
  return null;
};

const formatDataAsSections = (data, programTimezone) => {
  const filterValidEntries = (entries) =>
    entries.filter((entry) => entry.value !== null && entry.value !== undefined);

  const sections = [
    {
      title: "Basic details",
      details: filterValidEntries([
        { key: "id", label: "ID", value: data.id },
        { key: "identifier", label: "External ID", value: data.identifier || "-" },
        { key: "userId", label: "User ID", value: data.profileLogin },
        { key: "profileUuid", label: "Profile ID", value: data.profileId },
        { key: "deviceId", label: "Device ID", value: data.deviceId || "-" },
        { key: "campaignName", label: "Campaign Name", value: data.campaignName },
        {
          key: "campaignUUID",
          label: "Campaign UUID",
          value: data.campaignId || "-",
        },
        {
          key: "performanceDate",
          label: "Performance Date (UTC-6)",
          value:
            data.performanceDate && programTimezone
              ? moment
                  .tz(data.performanceDate, programTimezone)
                  .format("Do MMMM YYYY hh:mm A")
              : "-",
        },
        {
          key: "outcomes",
          label: "Outcomes",
          value: data.outcomes?.length
            ? data.outcomes.map((outcome) => ({
                name: convertToTitleCase(outcome.outcome),
                value: outcome.value ?? "N/A",
              }))
            : "-",
        },
        { key: "activityType", label: "Activity Type", value: data.engagementType },
        {
          key: "channel",
          label: "Channel",
          value: data.channel ? data.channel : "-",
        },
        { key: "status", label: "Status", value: data.status },
      ]),
      status: data.status,
    },
  ];
  const engagementTypeDetails = formatEngagmentTypeDetails(data, programTimezone);

  if (data.engagementType === "check-in") {
    sections[0].details = sections[0].details.filter(
      (detail) => detail.key !== "deviceId" && detail.key !== "identifier"
    );
  }

  if (engagementTypeDetails) {
    sections.push(engagementTypeDetails);
  }

  const additionalInfoDetails = formatAdditionalInfo(data.metadata?.additionalInfo);
  if (additionalInfoDetails.length > 0) {
    sections.push({
      title: "Additional Details",
      details: additionalInfoDetails,
    });
  }

  return sections;
};

const PerformanceDetailsContainer = ({
  title,
  loading,
  engagementData,
  showDetailViewOption = false,
  onDetailViewClick = () => {},
}) => {
  const { programTimezone } = useContext(GlobalConfigContext);

  const [sections, setSections] = useState([]);

  useEffect(() => {
    let _engagementData = {};
    if (engagementData) {
      _engagementData = engagementData;
    }
    const newSections = formatDataAsSections(_engagementData, programTimezone);
    setSections(newSections);
  }, [engagementData, programTimezone]);

  if (loading) {
    return (
      <LoadingSpinner
        size={{
          height: "30px",
          width: "30px",
        }}
        style={{ margin: "16px auto" }}
        data-testid="api-call-loading"
      />
    );
  }
  return (
    <div>
      <div className="performance-details-container">
        <div className="header-container">
          {title && <h4>{title}</h4>}
          {showDetailViewOption && (
            <ButtonGroupDynamic
              className="icon-button-group"
              items={[{ icon: "launch", onClick: onDetailViewClick }]}
              size="large"
            />
          )}
        </div>
        {sections.length > 0 && (
          <div className="performance-details-container">
            {sections.map((sectionProps, index) => (
              <PerformanceDetailsSection
                key={`section-${index}`}
                {...sectionProps}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
PerformanceDetailsContainer.propTypes = {
  title: PropTypes.string,
  engagementData: PropTypes.object.isRequired,
  engagementItem: PropTypes.object.isRequired,
  showFooter: PropTypes.bool,
  loading: PropTypes.bool,
  showDetailViewOption: PropTypes.bool,
  onDetailViewClick: PropTypes.func,
};

export default PerformanceDetailsContainer;
